import React, { useState, useEffect } from "react";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { CORSI, courseUrl } from "../mock";
import { trackQuizClick, courseNameFromPath, withTrackingParams } from "../lib/analytics";

const NAV_LINKS = [
  { label: "Metodo", href: "/#metodo" },
  { label: "All‑In", href: "/#all-in" },
  { label: "Formatore", href: "/#formatore" },
  { label: "Location", href: "/#location" },
  { label: "FAQ", href: "/#faq" },
];

export default function Navbar({ onOpenBooking }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [corsiOpen, setCorsiOpen] = useState(false);
  const [mobileCorsiOpen, setMobileCorsiOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reset menu on route change
  useEffect(() => {
    setOpen(false);
    setCorsiOpen(false);
    setMobileCorsiOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || location.pathname !== "/"
          ? "backdrop-blur-xl bg-[#0a0a0a]/80 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      {/* Announcement strip — apertura corsi */}
      <div
        className="w-full text-center text-[11px] sm:text-xs font-medium py-1.5 px-4 tracking-wide"
        style={{ background: "#d4ff3d", color: "#0a0a0a" }}
        data-testid="announcement-banner"
      >
        <span className="hidden sm:inline">◆&nbsp;&nbsp;</span>
        <strong className="uppercase tracking-widest">Novità</strong>
        <span className="mx-2">·</span>
        Corsi <strong>in aula (in presenza)</strong> oppure <strong>online one&#8209;to&#8209;one</strong> in sessioni da 4 ore<span className="hidden md:inline"> — con data personalizzabile in base alle tue esigenze</span>
      </div>
      <div className="container-max flex items-center justify-between py-4 lg:py-5">
        <Link to="/" className="flex items-center gap-2.5 group" aria-label="MyDigitalAction Experience">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center flex-shrink-0 group-hover:bg-lime transition-colors" style={{ transition: "background 0.25s ease" }}>
            <span className="font-bebas text-black text-lg leading-none">MDA</span>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-semibold text-white text-sm tracking-tight">
              MyDigitalAction<span style={{ color: "#d4ff3d" }}>.</span>
            </span>
            <span className="font-mono-label text-[9px] mt-0.5" style={{ color: "#d4ff3d", letterSpacing: "0.25em" }}>
              EXPERIENCE
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {/* Metodo, All-In */}
          {NAV_LINKS.slice(0, 2).map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}

          {/* Corsi dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setCorsiOpen(true)}
            onMouseLeave={() => setCorsiOpen(false)}
          >
            <button
              className="text-sm text-white/70 hover:text-white transition-colors inline-flex items-center gap-1"
              onClick={() => setCorsiOpen(!corsiOpen)}
            >
              Corsi
              <ChevronDown
                size={14}
                className={`transition-transform ${corsiOpen ? "rotate-180" : ""}`}
              />
            </button>

            {corsiOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3">
                <div className="w-[360px] bg-[#0f0f0f] border border-white/10 rounded-2xl p-2 shadow-2xl">
                  {/* All-In Experience featured */}
                  <a
                    href="/#prezzo"
                    className="block px-4 py-3 rounded-xl hover:bg-white/5 transition-colors border-b border-white/5 mb-1"
                    onClick={() => setCorsiOpen(false)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-mono-label text-[10px]" style={{ color: "#d4ff3d" }}>
                          Percorso completo
                        </div>
                        <div className="font-display text-white text-base mt-1">
                          All‑In Experience
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-white text-lg">€1.143</div>
                        <div className="text-white/40 text-[10px]">5 corsi</div>
                      </div>
                    </div>
                  </a>

                  <div className="font-mono-label text-white/40 text-[10px] px-4 py-2">
                    Corsi singoli
                  </div>

                  {CORSI.map((c) => (
                    <Link
                      key={c.slug}
                      to={courseUrl(c.slug)}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-white/5 transition-colors group"
                      onClick={() => setCorsiOpen(false)}
                    >
                      <span className="font-mono-label text-white/30 text-[11px] w-6">
                        {c.n}
                      </span>
                      <span className="flex-1 text-white/80 group-hover:text-white text-sm">
                        {c.title.split("—")[0].split("–")[0].trim()}
                      </span>
                      <span className="text-white/40 text-xs">€{c.price}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Formatore, Location, FAQ */}
          {NAV_LINKS.slice(2).map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <Link
            to="/area-studente"
            className="hidden md:inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
            data-testid="navbar-student-area"
          >
            Area studente
          </Link>
          <Link
            to={withTrackingParams("/quiz")}
            onClick={() => trackQuizClick({ courseName: courseNameFromPath(window.location.pathname) })}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border transition-colors"
            style={{ color: "#d4ff3d", borderColor: "rgba(212,255,61,0.4)", background: "rgba(212,255,61,0.06)" }}
            data-testid="navbar-quiz-btn"
          >
            <span style={{ fontSize: "13px" }}>✨</span>
            <span className="hidden sm:inline">Quiz gratuito</span>
            <span className="sm:hidden font-semibold">Quiz</span>
          </Link>
          <a
            href="/#prezzo"
            className="hidden md:inline-flex btn-primary"
            style={{ padding: "12px 20px", fontSize: 13 }}
          >
            Iscriviti ora <ArrowUpRight size={16} />
          </a>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/5 bg-[#0a0a0a]/95 backdrop-blur-xl max-h-[80vh] overflow-y-auto">
          <div className="container-max py-6 flex flex-col gap-2">
            {/* Quiz gratuito - highlighted */}
            <Link
              to={withTrackingParams("/quiz")}
              onClick={() => { trackQuizClick({ courseName: courseNameFromPath(window.location.pathname) }); setOpen(false); }}
              className="flex items-center justify-between px-4 py-3 rounded-xl mb-2 border transition-colors"
              style={{
                background: "linear-gradient(135deg, rgba(212,255,61,0.12) 0%, rgba(212,255,61,0.04) 100%)",
                borderColor: "rgba(212,255,61,0.4)",
              }}
              data-testid="mobile-menu-quiz-link"
            >
              <div className="flex items-center gap-3">
                <span style={{ fontSize: "18px" }}>✨</span>
                <div>
                  <div className="font-display font-semibold text-white text-base leading-tight">
                    Test di orientamento
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: "#d4ff3d" }}>
                    Gratuito · 2 minuti · scopri il corso giusto per te
                  </div>
                </div>
              </div>
              <ArrowUpRight size={18} style={{ color: "#d4ff3d" }} />
            </Link>

            {NAV_LINKS.slice(0, 2).map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-lg text-white/80 hover:text-white py-2"
              >
                {l.label}
              </a>
            ))}

            {/* Mobile Corsi accordion */}
            <div className="border-t border-white/5 pt-2">
              <button
                onClick={() => setMobileCorsiOpen(!mobileCorsiOpen)}
                className="w-full flex items-center justify-between text-lg text-white/80 hover:text-white py-2"
              >
                <span>Corsi</span>
                <ChevronDown
                  size={18}
                  className={`transition-transform ${mobileCorsiOpen ? "rotate-180" : ""}`}
                />
              </button>
              {mobileCorsiOpen && (
                <div className="pl-2 mt-1 space-y-1 border-l border-white/10">
                  <a
                    href="/#prezzo"
                    className="block px-3 py-2 text-white/70 hover:text-white text-sm"
                    onClick={() => setOpen(false)}
                  >
                    <span style={{ color: "#d4ff3d" }}>◆</span> All‑In Experience — €1.143
                  </a>
                  {CORSI.map((c) => (
                    <Link
                      key={c.slug}
                      to={courseUrl(c.slug)}
                      className="flex items-center gap-3 px-3 py-2 text-white/70 hover:text-white text-sm"
                      onClick={() => setOpen(false)}
                    >
                      <span className="font-mono-label text-white/30 text-[10px]">{c.n}</span>
                      <span className="flex-1">{c.title.split("—")[0].split("–")[0].trim()}</span>
                      <span className="text-white/40 text-xs">€{c.price}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {NAV_LINKS.slice(2).map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-lg text-white/80 hover:text-white py-2 border-t border-white/5"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/#prezzo"
              onClick={() => setOpen(false)}
              className="btn-primary mt-4 justify-center"
            >
              Iscriviti ora
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
